import React, { useEffect, useState } from 'react';
import { Plus, X, FloppyDisk, CircleNotch, Drop } from '@phosphor-icons/react';
import axios from '../../utils/axiosInstance';
import { vehicleLabel } from '../../utils/vehicleLabel';

const API = `${window.location.origin}/api`;
const today = () => new Date().toISOString().slice(0, 10);
const fmtKm = (v) => `${Number(v || 0).toLocaleString('pl-PL')} km`;

function OilStatus({ s }) {
    if (s.warning === 'no_history') return <span className="text-xs text-slate-500">Brak historii wymian</span>;
    const cls = s.warning === 'overdue' ? 'bg-red-100 text-red-700' : s.warning === 'soon' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700';
    const txt = s.warning === 'overdue' ? 'Wymiana zaległa' : `Do wymiany: ${fmtKm(s.km_until_oil_change)}`;
    return <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${cls}`} data-testid={`emp-oil-status-${s.pojazd_id}`}>{txt}</span>;
}

function AddModal({ vehicles, onClose, onSaved }) {
    const first = vehicles[0];
    const [form, setForm] = useState({ pojazd_id: first?.pojazd_id || '', data: today(), przebieg: first?.last_km ? String(first.last_km) : '', notes: '' });
    const [saving, setSaving] = useState(false);
    const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
    const pickVehicle = (id) => {
        const v = vehicles.find((x) => x.pojazd_id === id);
        setForm((p) => ({ ...p, pojazd_id: id, przebieg: v?.last_km ? String(v.last_km) : '' }));
    };
    const save = async () => {
        if (!form.pojazd_id || !form.data || !form.przebieg) return alert('Uzupełnij pojazd, datę i przebieg');
        setSaving(true);
        try {
            await axios.post(`${API}/wymiana-oleju/my`, { ...form, przebieg: parseFloat(form.przebieg), notes: form.notes || null });
            onSaved();
        } catch (e) {
            alert(e.response?.data?.detail || 'Błąd zapisu');
        } finally {
            setSaving(false);
        }
    };
    return (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 space-y-4" data-testid="emp-oil-modal">
                <div className="flex items-center justify-between">
                    <h3 className="text-lg font-bold text-slate-800">Nowa wymiana oleju</h3>
                    <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded-md" data-testid="emp-oil-modal-close"><X className="w-5 h-5" /></button>
                </div>
                <label className="block text-sm font-medium text-slate-700">Pojazd
                    <select value={form.pojazd_id} onChange={(e) => pickVehicle(e.target.value)} className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2" data-testid="emp-oil-pojazd">
                        {vehicles.map((v) => <option key={v.pojazd_id} value={v.pojazd_id}>{vehicleLabel(v)}</option>)}
                    </select>
                </label>
                <label className="block text-sm font-medium text-slate-700">Data
                    <input type="date" value={form.data} onChange={(e) => set('data', e.target.value)} className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2" data-testid="emp-oil-data" />
                </label>
                <label className="block text-sm font-medium text-slate-700">Przebieg (km)
                    <input type="number" min="0" value={form.przebieg} onChange={(e) => set('przebieg', e.target.value)} className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2" data-testid="emp-oil-przebieg" />
                </label>
                <label className="block text-sm font-medium text-slate-700">Notatki
                    <textarea rows={2} value={form.notes} onChange={(e) => set('notes', e.target.value)} className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2" data-testid="emp-oil-notes" />
                </label>
                <div className="flex justify-end gap-2">
                    <button onClick={onClose} className="btn-secondary text-sm" data-testid="emp-oil-cancel">Anuluj</button>
                    <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-md text-sm font-semibold disabled:opacity-60" data-testid="emp-oil-save">
                        {saving ? <CircleNotch className="w-4 h-4 animate-spin" /> : <FloppyDisk className="w-4 h-4" />} Zapisz
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function EmployeeWymianaOlejuTab() {
    const [items, setItems] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);

    const fetchAll = async () => {
        setLoading(true);
        try {
            const [w, s] = await Promise.all([
                axios.get(`${API}/wymiana-oleju/my`),
                axios.get(`${API}/przebiegi/status`),
            ]);
            setItems(w.data || []);
            setVehicles(s.data || []);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { fetchAll(); }, []);

    if (loading) return <div className="text-center py-12"><CircleNotch className="w-8 h-8 animate-spin mx-auto text-muted-foreground" /></div>;

    return (
        <div className="space-y-4" data-testid="emp-oil-container">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                    <h2 className="text-xl sm:text-2xl font-bold text-slate-800">Wymiana oleju</h2>
                    <p className="text-xs sm:text-sm text-slate-600 mt-1">Historia wymian oleju w Twoich pojazdach.</p>
                </div>
                {vehicles.length > 0 && (
                    <button onClick={() => setShowModal(true)} className="inline-flex items-center gap-1.5 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-md text-sm font-semibold" data-testid="emp-oil-add-btn">
                        <Plus className="w-4 h-4" weight="bold" /> Dodaj wymianę
                    </button>
                )}
            </div>

            {vehicles.length === 0 ? (
                <div className="text-center py-12 text-slate-500 bg-white rounded-lg border border-slate-200" data-testid="emp-oil-no-vehicles">Brak przypisanych pojazdów.</div>
            ) : (
                <div className="grid gap-2 sm:grid-cols-2">
                    {vehicles.map((v) => (
                        <div key={v.pojazd_id} className="bg-white border border-slate-200 rounded-lg p-3 flex items-center justify-between gap-2" data-testid={`emp-oil-vehicle-${v.pojazd_id}`}>
                            <div className="min-w-0">
                                <div className="font-semibold text-sm text-slate-800 truncate">{vehicleLabel(v)}</div>
                                <div className="text-xs text-slate-500">Ostatni przebieg: {fmtKm(v.last_km)}</div>
                            </div>
                            <OilStatus s={v} />
                        </div>
                    ))}
                </div>
            )}

            {items.length === 0 ? (
                <div className="text-center py-10 text-slate-500 bg-white rounded-lg border border-slate-200" data-testid="emp-oil-empty">Brak zapisanych wymian oleju.</div>
            ) : (
                <div className="bg-white rounded-lg border border-slate-200 divide-y divide-slate-100">
                    {items.map((i) => (
                        <div key={i.id} className="flex items-center gap-3 px-4 py-3" data-testid={`emp-oil-row-${i.id}`}>
                            <Drop className="w-5 h-5 text-amber-600 shrink-0" weight="fill" />
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-slate-800 truncate">{vehicleLabel(i.pojazd)}</div>
                                <div className="text-xs text-slate-500 truncate">{i.data && new Date(i.data).toLocaleDateString('pl-PL')}{i.notes && ` · ${i.notes}`}</div>
                            </div>
                            <div className="text-sm font-semibold text-slate-700 shrink-0">{fmtKm(i.przebieg)}</div>
                        </div>
                    ))}
                </div>
            )}

            {showModal && <AddModal vehicles={vehicles} onClose={() => setShowModal(false)} onSaved={() => { setShowModal(false); fetchAll(); }} />}
        </div>
    );
}
